/**
 * ContactPicker - Searchable contact picker with option to create new contacts
 *
 * Used for selecting attendees in meetings and other places where contacts are needed.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@components/ui/input';
import { Button } from '@components/ui/button';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import { useContactsStore } from '@stores/contactsStore';
import type { OrganizationContact } from '@services/api/contacts';

interface ContactPickerProps {
  organizationId: string;
  onSelect: (contact: OrganizationContact | { name: string; email?: string }) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** If true, allow creating new contacts inline */
  allowCreate?: boolean;
}

export function ContactPicker({
  organizationId,
  onSelect,
  placeholder = 'Search contacts...',
  className,
  disabled = false,
  allowCreate = true,
}: ContactPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newContactEmail, setNewContactEmail] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    searchResults,
    isSearching,
    searchContacts,
    createContact,
    clearSearchResults,
  } = useContactsStore();

  // Debounced search
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);

      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }

      if (value.trim().length >= 2) {
        searchTimeout.current = setTimeout(() => {
          void searchContacts(organizationId, value.trim());
        }, 300);
      } else {
        clearSearchResults();
      }
    },
    [organizationId, searchContacts, clearSearchResults]
  );

  const handleSelectContact = useCallback(
    (contact: OrganizationContact) => {
      onSelect(contact);
      setInputValue('');
      setIsOpen(false);
      clearSearchResults();
    },
    [onSelect, clearSearchResults]
  );

  const handleSelectAsText = useCallback(() => {
    if (inputValue.trim()) {
      // Check if input looks like an email
      const isEmail = inputValue.includes('@');
      if (isEmail) {
        onSelect({ name: '', email: inputValue.trim() });
      } else {
        onSelect({ name: inputValue.trim() });
      }
      setInputValue('');
      setIsOpen(false);
      clearSearchResults();
    }
  }, [inputValue, onSelect, clearSearchResults]);

  const handleCreateContact = useCallback(async () => {
    if (!inputValue.trim()) return;

    setCreateError(null);
    try {
      const newContact = await createContact({
        organizationId,
        name: inputValue.trim(),
        email: newContactEmail.trim() || undefined,
      });
      onSelect(newContact);
      setInputValue('');
      setNewContactEmail('');
      setShowCreateForm(false);
      setIsOpen(false);
      clearSearchResults();
    } catch (error) {
      setCreateError(
        error instanceof Error ? error.message : 'Failed to create contact'
      );
    }
  }, [
    inputValue,
    newContactEmail,
    organizationId,
    createContact,
    onSelect,
    clearSearchResults,
  ]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setShowCreateForm(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasResults = searchResults.length > 0;
  const showDropdown =
    isOpen && (hasResults || isSearching || inputValue.trim().length >= 2);

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      <Input
        type="text"
        value={inputValue}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full"
      />

      {showDropdown && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-auto rounded-md border border-neutral-200 bg-white shadow-lg dark:border-neutral-700 dark:bg-neutral-800">
          {isSearching ? (
            <div className="flex items-center justify-center py-4">
              <LoadingSpinner size="sm" />
              <span className="ml-2 text-sm text-neutral-500">Searching...</span>
            </div>
          ) : hasResults ? (
            <>
              {searchResults.map((contact) => (
                <button
                  key={contact.id}
                  type="button"
                  onClick={() => handleSelectContact(contact)}
                  className="flex w-full items-start px-3 py-2 text-left hover:bg-neutral-100 dark:hover:bg-neutral-700"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-neutral-900 dark:text-neutral-100">
                      {contact.name || contact.email || 'Unnamed'}
                    </div>
                    {contact.email && contact.name && (
                      <div className="truncate text-sm text-neutral-500">
                        {contact.email}
                      </div>
                    )}
                    {contact.company && (
                      <div className="truncate text-xs text-neutral-400">
                        {contact.company}
                      </div>
                    )}
                  </div>
                </button>
              ))}

              {/* Option to use as-is (for quick add) */}
              {inputValue.trim() && (
                <div className="border-t border-neutral-200 dark:border-neutral-700">
                  <button
                    type="button"
                    onClick={handleSelectAsText}
                    className="flex w-full items-center px-3 py-2 text-left text-sm text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-700"
                  >
                    <span className="mr-2">+</span>
                    Use &ldquo;{inputValue.trim()}&rdquo; as name
                  </button>
                </div>
              )}
            </>
          ) : inputValue.trim().length >= 2 ? (
            <div className="px-3 py-4">
              <p className="mb-2 text-sm text-neutral-500">
                No contacts found for &ldquo;{inputValue}&rdquo;
              </p>

              {allowCreate && !showCreateForm && (
                <div className="space-y-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCreateError(null);
                      setShowCreateForm(true);
                    }}
                    className="w-full"
                  >
                    Create new contact
                  </Button>
                  <button
                    type="button"
                    onClick={handleSelectAsText}
                    className="w-full text-sm text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100"
                  >
                    or use &ldquo;{inputValue.trim()}&rdquo; as-is
                  </button>
                </div>
              )}

              {allowCreate && showCreateForm && (
                <div className="space-y-2">
                  <div className="text-sm font-medium">Create contact</div>
                  <div className="text-sm text-neutral-500">
                    Name: {inputValue.trim()}
                  </div>
                  <Input
                    type="email"
                    value={newContactEmail}
                    onChange={(e) => setNewContactEmail(e.target.value)}
                    placeholder="Email (optional)"
                    className="text-sm"
                  />
                  {createError && (
                    <p className="text-sm text-red-600 dark:text-red-400">
                      {createError}
                    </p>
                  )}
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleCreateContact}
                      className="flex-1"
                    >
                      Create
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setCreateError(null);
                        setShowCreateForm(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default ContactPicker;
